import React, { useState, useEffect } from "react";
import { Form, Input, Button, Upload, message, Typography, Space, Tabs, Table, Modal, Popconfirm, Avatar, Dropdown, Select } from "antd";
import { UploadOutlined, ShopOutlined, PlusOutlined, DeleteOutlined, EditOutlined, FilePdfOutlined, LogoutOutlined, UserOutlined, EditFilled, EyeOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../AuthPage/Login.css";
import "./VendorDashboard.css";
import NewLogo from "../../assets/images/logonew.jpg";

const { Text } = Typography;

const VendorDashboard = () => {
  // Auth state
  const [token, setToken] = useState(localStorage.getItem("userToken"));
  /* "login" or "signup". Registration used to be reachable only by clicking
     through from the sign-in form, so there was no URL that opened it - which
     left the marketing site with nothing to point "Sign up as vendor" at.
     ?mode=signup gives it one. Read once, as the initial value: this is the
     starting tab, and the in-page switcher owns it from then on. */
  const [authMode, setAuthMode] = useState(() =>
    new URLSearchParams(window.location.search).get("mode") === "signup"
      ? "signup"
      : "login"
  );
  const [authLoading, setAuthLoading] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authName, setAuthName] = useState("");
  const [authOtp, setAuthOtp] = useState("");
  /* Set when the API says the token is good but the ACCOUNT is not a vendor.
     Its own screen, not a sign-out: the session is valid and the fix is one
     button, so throwing it away would be the wrong answer to the wrong
     question. See the catch in fetchVendorProfile. */
  const [accessDenied, setAccessDenied] = useState(false);
  const [enablingVendor, setEnablingVendor] = useState(false);
  /* "vendor" or "admin" - who is looking. An admin is inspecting the portal
     rather than trading through it, and the page says so. */
  const [viewerRole, setViewerRole] = useState(null);

  // Dashboard state
  const [activeTab, setActiveTab] = useState("profile");
  const [profileForm] = Form.useForm();
  const [productForm] = Form.useForm();
  
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [submittingProduct, setSubmittingProduct] = useState(false);
  
  const [vendorData, setVendorData] = useState(null);
  const [trialDetails, setTrialDetails] = useState({});
  const [profileFileList, setProfileFileList] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [savingDocs, setSavingDocs] = useState(false);
  
  const [products, setProducts] = useState([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [productFileList, setProductFileList] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Profile edit lock state
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // Subscription upgrade
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null); // { hasSubscription, currentPlan, upgradePlans }
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState(null);

  // Dropdown states
  const [shapes, setShapes] = useState([]);
  const [colors, setColors] = useState([]);
  const [cases, setCases] = useState([]);
  const [showCreateNameModal, setShowCreateNameModal] = useState(false);
  const [createNameType, setCreateNameType] = useState("");
  const [createNameApiPrefix, setCreateNameApiPrefix] = useState("");
  const [newOptionValue, setNewOptionValue] = useState("");
  const [creatingOption, setCreatingOption] = useState(false);

  const navigate = useNavigate();
  const url = process.env.REACT_APP_API_BASE_URL;

  const fetchDropdownData = async (currentToken) => {
    try {
      const [shapeRes, colorRes, caseRes] = await Promise.all([
        axios.get(`${url}/auth/v1/shapes`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }),
        axios.get(`${url}/auth/v1/colors`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }),
        axios.get(`${url}/auth/v1/cases`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }),
      ]);
      setShapes(shapeRes.data.Shapes || []);
      setColors(colorRes.data.Colors || []);
      setCases(caseRes.data.Cases || []);
    } catch (error) {
      console.error("Failed to fetch dropdown data:", error);
    }
  };

  const handleOpenCreateNameModal = (prefix, type) => {
    setCreateNameApiPrefix(prefix);
    setCreateNameType(type);
    setNewOptionValue("");
    setShowCreateNameModal(true);
  };

  const handleCreateOption = async () => {
    if (!newOptionValue.trim()) {
      message.error(`Please enter ${createNameType.toLowerCase()} name`);
      return;
    }
    setCreatingOption(true);
    try {
      await axios.post(
        `${url}/auth/v1/${createNameApiPrefix}`,
        { name: newOptionValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`${createNameType} created successfully!`);
      await fetchDropdownData(token);
      productForm.setFieldsValue({ [createNameApiPrefix]: newOptionValue });
      setShowCreateNameModal(false);
    } catch (error) {
      message.error(`Failed to create ${createNameType.toLowerCase()}`);
    } finally {
      setCreatingOption(false);
    }
  };

  // Fetch Vendor Profile
  const fetchVendorProfile = async (currentToken) => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/auth/v1/vendor-profile`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (res.data.status) {
        const { vendor, trialStartDate, trialEndDate, trialDaysRemaining, isTrialActive, isSubscribed, premiumValidUpto, isApproved, isActiveListing, currentPlan, canUpgrade, profileType } = res.data;
        setAccessDenied(false);
        setViewerRole(profileType || "vendor");
        setVendorData(vendor);
        setTrialDetails({
          trialStartDate,
          trialEndDate,
          trialDaysRemaining,
          isTrialActive,
          isSubscribed,
          premiumValidUpto,
          isApproved,
          isActiveListing,
          currentPlan,
          canUpgrade
        });

        profileForm.setFieldsValue({
          name: vendor.name,
          description: vendor.description,
          contactPhone: vendor.contactPhone,
          contactEmail: vendor.contactEmail,
          category: vendor.category
        });

        setDocs(vendor.documents || []);

        if (vendor.images && vendor.images.length > 0) {
          setProfileFileList(vendor.images.map((img, i) => ({
            uid: `-${i}`,
            name: `Image_${i}.png`,
            status: "done",
            url: img
          })));
        }
      }
    } catch (error) {
      const status = error.response?.status;
      const apiMessage = error.response?.data?.message;

      /* Three unrelated failures used to share one branch, and every one of
         them ended in a sign-out - which is how an admin, and any signed-in
         buyer who followed a link here, got ejected from the whole app just
         for opening this page.

           NOT_A_VENDOR   the token is fine, the ACCOUNT is a buyer. Offer
                          the switch; keep the session.
           bad token      sign out, as before. The 500 is not a typo: this
                          API's auth middleware answers an invalid token
                          with 500 "Invalid Token" rather than 401, so
                          testing 401 alone misses the usual case.
           anything else  a server problem, not an auth one. Say so and
                          leave the session alone. */
      if (error.response?.data?.code === "NOT_A_VENDOR") {
        setAccessDenied(true);
      } else if (status === 401 || apiMessage === "Invalid Token") {
        handleLogout();
      } else {
        message.error(apiMessage || "Failed to fetch vendor profile");
      }
    } finally {
      setLoading(false);
    }
  };

  /* The way in for someone who reached the portal already signed in as a
     buyer. The signed-OUT route is the profileType flag handleVerifyOtp
     sends; this is the same upgrade, asked for explicitly, because there is
     no sign-in left to attach it to. */
  const handleEnableVendorAccess = async () => {
    setEnablingVendor(true);
    try {
      await axios.post(
        `${url}/auth/v1/vendor-access`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccessDenied(false);
      message.success("Vendor portal enabled for this account.");
      await Promise.all([
        fetchVendorProfile(token),
        fetchVendorProducts(token),
        fetchDropdownData(token),
      ]);
    } catch (error) {
      message.error(
        error.response?.data?.message || "Could not enable the vendor portal for this account."
      );
    } finally {
      setEnablingVendor(false);
    }
  };

  // ── Subscription upgrade ────────────────────────────────────────────────
  // Opens the picker: asks the API which plans sit above the current one.
  const openUpgradeModal = async () => {
    setUpgradeModalOpen(true);
    setUpgradeLoading(true);
    try {
      const res = await axios.get(`${url}/auth/v1/payment/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpgradeInfo(res.data);
    } catch (error) {
      message.error(error.response?.data?.message || "Could not load upgrade options");
      setUpgradeModalOpen(false);
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Switches the live Stripe subscription to the chosen plan. Stripe charges
  // only the prorated difference; the renewal date stays the same. If there is
  // no Stripe subscription to move (e.g. access was granted by an admin) the API
  // answers 409 and we fall back to a normal checkout for that plan.
  const handleUpgrade = async (plan) => {
    setUpgradingPlanId(plan._id);
    try {
      const res = await axios.post(
        `${url}/auth/v1/payment/upgrade-subscription`,
        { id: plan._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(res.data.message || `Upgraded to ${plan.name}`);
      setUpgradeModalOpen(false);
      fetchVendorProfile(token);
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.requiresCheckout) {
        try {
          const co = await axios.post(
            `${url}/auth/v1/payment/checkout-session`,
            { id: plan._id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (co.data.success && co.data.checkoutUrl) {
            window.location.href = co.data.checkoutUrl;
            return;
          }
          message.error(co.data.message || "Failed to start checkout");
        } catch (coErr) {
          message.error(coErr.response?.data?.message || "Failed to start checkout");
        }
      } else {
        message.error(error.response?.data?.message || "Upgrade failed");
      }
    } finally {
      setUpgradingPlanId(null);
    }
  };

  // Fetch Vendor Products
  const fetchVendorProducts = async (currentToken) => {
    try {
      setProductsLoading(true);
      const res = await axios.get(`${url}/auth/v1/myProducts`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (res.data.status) {
        setProducts(res.data.products || []);
      }
    } catch (error) {
      console.error("Error fetching products", error);
    } finally {
      setProductsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchVendorProfile(token);
      fetchVendorProducts(token);
      fetchDropdownData(token);
    }
  }, [token]);

  // Auth Handlers
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!authEmail) {
      message.warning("Please enter your email");
      return;
    }
    setAuthLoading(true);
    try {
      if (authMode === "signup") {
        const res = await axios.post(`${url}/auth/v1/signup`, {
          email: authEmail.toLowerCase(),
          name: authName,
          profileType: "vendor"
        });
        if (res.data.success) {
          setOtpRequested(true);
          message.success("Registration OTP sent successfully");
        } else {
          message.error(res.data.message || "Failed to send OTP");
        }
      } else {
        const res = await axios.post(`${url}/auth/v1/sendotp`, {
          email: authEmail.toLowerCase()
        });
        if (res.data.success) {
          setOtpRequested(true);
          message.success("Login OTP sent successfully");
        } else {
          message.error(res.data.message || "Failed to send OTP");
        }
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Verification request failed");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!authOtp) {
      message.warning("Please enter the verification code");
      return;
    }
    setAuthLoading(true);
    try {
      const res = await axios.post(`${url}/auth/v1/verifyotp`, {
        email: authEmail.toLowerCase(),
        otp: authOtp,
        /* Which door this sign-in came through. Registration already said
           so; sign-in did not, which is why an existing buyer account could
           only ever be refused here - the server had no way to tell "wrong
           person" from "right person, wrong role". It is a request, not a
           claim: the server decides whether to honour it, and vendor is the
           only role it will ever write from this field. */
        profileType: "vendor"
      });
      if (res.data.status) {
        const userToken = res.data.userToken;
        localStorage.setItem("userToken", userToken);
        setAccessDenied(false);
        setToken(userToken);
        message.success(
          res.data.promotedToVendor
            ? "Signed in. This account can now use the vendor portal."
            : "Successfully authenticated!"
        );
      } else {
        message.error("Invalid verification code. Please try again.");
      }
    } catch (error) {
      message.error("Authentication failed. Please verify credentials.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    setToken(null);
    setAccessDenied(false);
    setViewerRole(null);
    setOtpRequested(false);
    setAuthEmail("");
    setAuthOtp("");
    setAuthName("");
    setVendorData(null);
    setProducts([]);
  };

  // Vendor Profile Handlers
  const MAX_DOCS = 10;

  /* Uploads immediately and appends to the local list; the list is only
     persisted when the vendor presses Save, so nothing changes on the
     public page until they commit. */
  const handleDocUpload = async (file) => {
    if (docs.length >= MAX_DOCS) {
      message.error(`You can upload at most ${MAX_DOCS} documents.`);
      return;
    }
    setSavingDocs(true);
    try {
      const form = new FormData();
      form.append("image", file);
      const res = await axios.post(`${url}/auth/v1/upload-img`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setDocs((prev) => [
        ...prev,
        {
          url: res.data.fileUrl,
          // Filename minus extension as a starting title, editable below.
          title: file.name.replace(/\.pdf$/i, "").slice(0, 120),
        },
      ]);
    } catch (err) {
      message.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to upload the PDF"
      );
    } finally {
      setSavingDocs(false);
    }
  };

  const handleSaveDocs = async () => {
    setSavingDocs(true);
    try {
      await axios.put(
        `${url}/auth/v1/vendor-profile`,
        { documents: docs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Documents saved");
      setDocModalOpen(false);
      fetchVendorProfile(token);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Failed to save documents"
      );
    } finally {
      setSavingDocs(false);
    }
  };

  const handleProfileSubmit = async (values) => {
    setLoading(true);
    try {
      const imageUrls = profileFileList.map(file => file.url || file.response?.fileUrl).filter(Boolean);

      const payload = {
        ...values,
        images: imageUrls
      };

      const res = await axios.put(`${url}/auth/v1/vendor-profile`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (res.data.status) {
        message.success("Vendor profile updated successfully!");
        setIsEditingProfile(false);
        fetchVendorProfile(token);
      }
    } catch (error) {
      message.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  // Product Operations
  const handleCreateOrUpdateProduct = async (values) => {
    setSubmittingProduct(true);
    try {
      const imageUrls = productFileList.map(file => file.url || file.response?.fileUrl).filter(Boolean);
      const payload = {
        ...values,
        images: imageUrls,
        suggestions: values.suggestions ? values.suggestions.split(",").map(s => s.trim()) : [],
        notAvail: values.notAvail ? values.notAvail.split(",").map(s => s.trim()) : []
      };

      let res;
      if (editingProduct) {
        res = await axios.patch(`${url}/auth/v1/product/${editingProduct._id}`, payload, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        });
      } else {
        res = await axios.post(`${url}/auth/v1/product`, payload, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        });
      }

      if (res.data.status) {
        message.success(editingProduct ? "Product updated successfully!" : "Product listed successfully!");
        setIsProductModalOpen(false);
        productForm.resetFields();
        setProductFileList([]);
        setEditingProduct(null);
        fetchVendorProducts(token);
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to save product");
    } finally {
      setSubmittingProduct(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await axios.delete(`${url}/auth/v1/product/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      message.success("Product deleted successfully");
      fetchVendorProducts(token);
    } catch (error) {
      message.error("Failed to delete product");
    }
  };

  const handleCustomUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await axios.post(`${url}/auth/v1/upload-img`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      if (res.data.fileUrl) {
        onSuccess({ fileUrl: res.data.fileUrl });
        message.success("Image uploaded successfully");
      } else {
        onError();
      }
    } catch (error) {
      onError();
      message.error("Image upload failed");
    }
  };

  const openAddProductModal = () => {
    setEditingProduct(null);
    productForm.resetFields();
    setProductFileList([]);
    setIsProductModalOpen(true);
  };

  const openEditProductModal = (record) => {
    setEditingProduct(record);
    productForm.setFieldsValue({
      name: record.name,
      height: record.height,
      width: record.width,
      mass: record.mass,
      mouldNumber: record.mouldNumber,
      alphaCode: record.alphaCode,
      description: record.description,
      pdfLink: record.pdfLink,
      suggestions: record.suggestions ? record.suggestions.join(", ") : "",
      notAvail: record.notAvail ? record.notAvail.join(", ") : "",
      shape: record.shape,
      color: record.color,
      case: record.case
    });

    if (record.images && record.images.length > 0) {
      setProductFileList(record.images.map((img, i) => ({
        uid: `-${i}`,
        name: `ProdImage_${i}.png`,
        status: "done",
        url: img
      })));
    } else {
      setProductFileList([]);
    }
    setIsProductModalOpen(true);
  };

  const productColumns = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      render: (text, record) => {
        const imageUrl = record.images && record.images.length > 0 ? record.images[0] : null;
        return (
          <Space>
            {imageUrl ? (
              <img src={imageUrl} alt="Product" style={{ width: 40, height: 40, borderRadius: 4, objectFit: "cover" }} />
            ) : (
              <Avatar shape="square" size={40} icon={<ShopOutlined />} />
            )}
            <Text style={{ fontWeight: 600 }}>{text}</Text>
          </Space>
        );
      },
    },
    {
      title: "Mould #",
      dataIndex: "mouldNumber",
      key: "mouldNumber",
      align: "left",
    },
    {
      title: "Alpha Code",
      dataIndex: "alphaCode",
      key: "alphaCode",
      align: "left",
    },
    {
      title: "Dimensions (H x W)",
      key: "dimensions",
      align: "left",
      render: (_, record) => `${record.height || "—"} x ${record.width || "—"} mm`,
    },
    {
      title: "Actions",
      key: "actions",
      align: "left",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => openEditProductModal(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this product?"
            onConfirm={() => handleDeleteProduct(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const canAddProduct = trialDetails.isTrialActive || trialDetails.isSubscribed;

  /* `danger` uses antd's error token instead of raw `red`, and drops the
     bold weight: signing out is not a destructive action that needs to
     shout. Menu.Item and Dropdown's `overlay` prop are both deprecated in
     antd v5, so this is the items API. */
  const profileMenuItems = [
    {
      key: "logout",
      danger: true,
      icon: <LogoutOutlined />,
      label: "Log out",
      onClick: handleLogout,
    },
  ];

  // 1. Authenticated User Login layout matching standard page style
  if (!token) {
    return (
      <div className="login-page" style={{ boxSizing: "border-box" }}>
        <div className="container center" style={{ boxSizing: "border-box" }}>
          <div className="new-login-page" style={{ boxSizing: "border-box" }}>
            <div className="right-content">
              <img loading="lazy" src={NewLogo} alt="collings-denture" />
            </div>

            <div className="left-content">
              {/* Mirror of the toggle on the user sign-in, so someone who
                  lands on the wrong one can cross over instead of hunting
                  for a URL. */}
              <div className="auth-toggle" role="tablist" aria-label="Account type">
                <button
                  type="button"
                  role="tab"
                  aria-selected="false"
                  className="auth-toggle-option"
                  /* Cross over at the same step, not back to the start:
                     from vendor registration this went to user SIGN-IN,
                     so someone registering had to find the signup link
                     again after switching. */
                  onClick={() =>
                    navigate(authMode === "signup" ? "/signup" : "/")
                  }
                >
                  User
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected="true"
                  className="auth-toggle-option auth-toggle-active"
                >
                  Vendor
                </button>
              </div>

              <h2>{authMode === "signup" ? "Vendor registration" : "Vendor sign in"}</h2>
              <p>
                {authMode === "signup"
                  ? "Find The Ideal Set of Teeth Instantly"
                  : "Already have a customer account? Sign in with that email — it will be enabled for the vendor portal."}
              </p>

              <form onSubmit={otpRequested ? (e) => { e.preventDefault(); handleVerifyOtp(); } : handleSendOtp}>
                {authMode === "signup" && !otpRequested && (
                  <div className="input-group">
                    <Input
                      type="text"
                      value={authName}
                      onChange={(e) => setAuthName(e.target.value)}
                      placeholder="Enter business / vendor name"
                      required
                    />
                  </div>
                )}

                <div className="input-group">
                  <Input
                    type="email"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    required
                    disabled={otpRequested}
                    placeholder="Enter vendor email"
                  />
                </div>

                {!otpRequested && (
                  <Button type="primary" htmlType="submit" loading={authLoading} style={{ width: "100%" }}>
                    {authLoading ? "Sending OTP..." : "Get Started"}
                  </Button>
                )}
              </form>

              {otpRequested && (
                <>
                  <div className="input-group" style={{ marginTop: 20 }}>
                    <Input
                      type="text"
                      value={authOtp}
                      onChange={(e) => setAuthOtp(e.target.value)}
                      required
                      placeholder="Enter 6-digit OTP code"
                    />
                  </div>

                  <Button
                    type="primary"
                    htmlType="button"
                    loading={authLoading}
                    onClick={handleVerifyOtp}
                    style={{ marginTop: 10, width: "100%" }}
                  >
                    {authLoading ? "Verifying..." : "Verify OTP Code"}
                  </Button>
                </>
              )}

              {/* Tabs selector links styled like signin/signup link switcher */}
              {/* #bdb5b5 and #3b82f6 were picked to sit on the old dark auth
                  panel. That panel is light now, so these follow the palette
                  via .auth-alt and .ant-btn-link instead. */}
              <div className="auth-alt">
                {authMode === "signup" ? (
                  <>
                    Already have a Vendor Account?{" "}
                    <Button
                      type="link"
                      onClick={() => {
                        setAuthMode("login");
                        setOtpRequested(false);
                      }}
                    >
                      Sign in
                    </Button>
                  </>
                ) : (
                  <>
                    Register as a Vendor?{" "}
                    {/* Colour comes from .login-page .ant-btn-link now. The
                        inline #3b82f6 was the same blue as the fill this
                        button was wrongly given, so the label disappeared. */}
                    <Button
                      type="link"
                      onClick={() => {
                        setAuthMode("signup");
                        setOtpRequested(false);
                      }}
                    >
                      Sign up
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* 1b. Signed in, valid session, but the account is still a buyer.
         Reached by following a link to the portal while already signed in
         through the customer side - there was no sign-in for the vendor
         flag to ride along with, so the switch is offered here instead.
         Sitting between the two branches on purpose: they have a token, so
         the sign-in form would be wrong, and they have no vendor profile,
         so the dashboard would render empty. */
  if (accessDenied) {
    return (
      <div className="login-page" style={{ boxSizing: "border-box" }}>
        <div className="container center" style={{ boxSizing: "border-box" }}>
          <div className="new-login-page" style={{ boxSizing: "border-box" }}>
            <div className="right-content">
              <img loading="lazy" src={NewLogo} alt="collings-denture" />
            </div>

            <div className="left-content">
              <h2>Sell on Tooth Finder</h2>
              <p>
                You are signed in with a customer account. Turn on the vendor
                portal to list your moulds — same login, same email, nothing
                to sign up for again.
              </p>

              <Button
                type="primary"
                loading={enablingVendor}
                onClick={handleEnableVendorAccess}
                style={{ marginTop: 10, width: "100%" }}
              >
                {enablingVendor ? "Enabling..." : "Enable vendor portal"}
              </Button>

              <div className="auth-alt">
                Wrong account?{" "}
                <Button type="link" onClick={handleLogout}>
                  Sign in as someone else
                </Button>
              </div>

              <div className="auth-alt">
                <Button type="link" onClick={() => navigate("/home")}>
                  Back to browsing moulds
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. Authenticated Vendor View (Dashboard Layout with Custom Header)
  return (
    <div className="vp-page">
      <header className="vp-header">
        <div className="vp-brand">
          <img src={NewLogo} alt="Tooth Finder" />
          {/* Plain text rather than a filled badge pill welded to the logo. */}
          {/* An admin gets the portal too, and is told which one they are
              looking at - the page is otherwise identical to a vendor's own
              and reads as if it were theirs. */}
          <span className="vp-brand-label">
            {viewerRole === "admin" ? "Vendor portal · admin view" : "Vendor portal"}
          </span>
        </div>

        <Dropdown
          menu={{ items: profileMenuItems }}
          placement="bottomRight"
          trigger={["hover"]}
          overlayClassName="vp-account-menu"
        >
          <div className="vp-account">
            <Avatar style={{ backgroundColor: "#2F45DA" }} icon={<UserOutlined />} />
            <div className="vp-account-text">
              <span className="vp-account-name">{vendorData?.name || "Vendor"}</span>
              <span className="vp-account-mail">
                {vendorData?.contactEmail || "Signed in"}
              </span>
            </div>
          </div>
        </Dropdown>
      </header>

      <div className="vp-shell">
        <div className="vp-panel">
          {/* One row of states, not two stacked full-width alert slabs.
              These are the three facts a vendor opens this page for: am I
              listed, how long have I got, am I paying. */}
          <div className="vp-status">
            <div className="vp-states">
              <div className="vp-state">
                <span className="vp-state-label">Directory listing</span>
                <span className="vp-state-value">
                  <span
                    className={`vp-dot ${
                      trialDetails.isActiveListing ? "vp-dot-good" : "vp-dot-warn"
                    }`}
                  />
                  {trialDetails.isActiveListing ? "Live" : "Not listed"}
                </span>
              </div>

              <div className="vp-state">
                <span className="vp-state-label">Free trial</span>
                <span className="vp-state-value">
                  <span
                    className={`vp-dot ${
                      trialDetails.isTrialActive ? "vp-dot-good" : ""
                    }`}
                  />
                  {trialDetails.isTrialActive
                    ? `${trialDetails.trialDaysRemaining} days left`
                    : "Ended"}
                </span>
              </div>

              <div className="vp-state">
                <span className="vp-state-label">Subscription</span>
                <span className="vp-state-value">
                  <span
                    className={`vp-dot ${
                      trialDetails.isSubscribed ? "vp-dot-good" : ""
                    }`}
                  />
                  {trialDetails.isSubscribed && trialDetails.premiumValidUpto
                    ? `Until ${new Date(
                        trialDetails.premiumValidUpto
                      ).toLocaleDateString()}`
                    : "None"}
                </span>
              </div>
            </div>

            <div className="vp-status-actions">
              {/* Vendors already paying can move up a tier without cancelling.
                  Shown whenever there is an active subscription; the modal
                  itself says if nothing higher exists yet. */}
              {trialDetails.isSubscribed && (
                <Button type="primary" onClick={openUpgradeModal}>
                  Upgrade
                </Button>
              )}

              {!trialDetails.isTrialActive &&
                !trialDetails.isSubscribed &&
                (() => {
                  const requireApproval =
                    process.env.REACT_APP_REQUIRE_VENDOR_APPROVAL?.toLowerCase() ===
                    "true";
                  if (requireApproval && !trialDetails.isApproved) return null;
                  return (
                    /* Was `danger`, which painted the one thing the page wants
                       you to do in the colour reserved for destructive actions. */
                    <Button type="primary" onClick={() => navigate("/plans")}>
                      Subscribe to list products
                    </Button>
                  );
                })()}

              {trialDetails.isActiveListing && vendorData?.slug && (
                <a
                  className="vp-view-listing"
                  href={`/vendor/${vendorData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View public listing
                </a>
              )}
            </div>

            {!trialDetails.isActiveListing && (
              <p className="vp-status-note">
                {!trialDetails.isApproved
                  ? "An administrator is reviewing your profile. You can keep editing it in the meantime; it appears in the directory once approved and you have an active trial or subscription."
                  : "Your profile is approved but not yet listed — it appears in the directory once you have an active trial or subscription."}
              </p>
            )}
          </div>

          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            {/* Tab 1: Profile */}
            <Tabs.TabPane tab="Business profile" key="profile">
              <div className="vp-section-head">
                <div>
                  <h2 className="vp-section-title">Business profile</h2>
                  <p className="vp-section-sub">
                    What buyers see when your lab appears in the directory.
                  </p>
                </div>
                {!isEditingProfile && (
                  <Space>
                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={() => setDocModalOpen(true)}
                    >
                      Documents{docs.length ? ` (${docs.length})` : ""}
                    </Button>
                    <Button
                      type="primary"
                      icon={<EditFilled />}
                      onClick={() => setIsEditingProfile(true)}
                    >
                      Edit profile
                    </Button>
                  </Space>
                )}
              </div>

              {isEditingProfile ? (
                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleProfileSubmit}
                  /* Marks the two optional fields instead of the two required
                     ones, so every label starts on the same left edge. */
                  requiredMark="optional"
                >
                  <div className="vp-form-grid">
                    <Form.Item
                      className="vp-form-wide"
                      name="name"
                      label="Business name"
                      /* Locked after verification: an admin approved this
                         specific business, and the public URL is derived
                         from the name and then frozen. Says why rather
                         than presenting a field that silently rejects. */
                      extra={
                        trialDetails.isApproved
                          ? "Locked because your account is verified. Contact support if it needs to change."
                          : "You can change this until your account is verified."
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please enter your business name",
                        },
                      ]}
                    >
                      <Input
                        placeholder="e.g. Acme Dental Laboratory"
                        disabled={trialDetails.isApproved}
                      />
                    </Form.Item>

                    <Form.Item
                      className="vp-form-wide"
                      name="category"
                      label="Specialisation"
                    >
                      <Input placeholder="e.g. Dentures, Crown & Bridge, Orthodontics" />
                    </Form.Item>

                    <Form.Item name="contactPhone" label="Contact phone">
                      <Input placeholder="Phone number" />
                    </Form.Item>

                    <Form.Item
                      name="contactEmail"
                      label="Contact email"
                      rules={[
                        {
                          required: true,
                          message: "Please enter a contact email address",
                        },
                      ]}
                    >
                      <Input type="email" placeholder="Email address" />
                    </Form.Item>

                    <Form.Item
                      className="vp-form-wide"
                      name="description"
                      label="About the business"
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Equipment, turnaround times, what you specialise in."
                      />
                    </Form.Item>

                    <Form.Item className="vp-form-wide" label="Portfolio images">
                      <Upload
                        listType="picture-card"
                        fileList={profileFileList}
                        onChange={({ fileList: list }) => {
                          const updatedList = list.map((file) => {
                            if (file.status === "done" && file.response && !file.url) {
                              file.url = file.response.fileUrl;
                            }
                            if (!file.url && !file.thumbUrl && file.originFileObj) {
                              file.thumbUrl = URL.createObjectURL(file.originFileObj);
                            }
                            return file;
                          });
                          setProfileFileList(updatedList);
                        }}
                        customRequest={handleCustomUpload}
                        onRemove={(file) => {
                          setProfileFileList((prev) =>
                            prev.filter((item) => item.uid !== file.uid)
                          );
                        }}
                      >
                        {profileFileList.length < 5 && (
                          <div>
                            <UploadOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                          </div>
                        )}
                      </Upload>
                    </Form.Item>
                  </div>

                  <div className="vp-form-foot">
                    <Button
                      onClick={() => {
                        setIsEditingProfile(false);
                        profileForm.resetFields();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Save profile
                    </Button>
                  </div>
                </Form>
              ) : (
                /* Read state. This used to be the same form with every input
                   `disabled`, so real values rendered as grey placeholder
                   text inside dead boxes: a filled-in profile looked empty.
                   A record is type, not a form nobody can type into.

                   Laid out as the reference mock's bento rather than one
                   flat definition list: identity and contact on the left,
                   the long-form about plus the two asset collections on the
                   right. A single <dl> gave a one-line phone number the
                   same visual weight as a five-paragraph description. */
                <div className="vp-bento">
                  <div className="vp-bento-col">
                    <section className="vp-card vp-identity">
                      <h3 className="vp-identity-name">
                        {vendorData?.name || "Unnamed business"}
                      </h3>
                      {vendorData?.category ? (
                        <span className="vp-identity-cat">
                          {vendorData.category}
                        </span>
                      ) : (
                        <span className="vp-empty">No specialisation set</span>
                      )}
                    </section>

                    <section className="vp-card">
                      <h3 className="vp-card-title">Contact</h3>
                      <ul className="vp-contact">
                        <li>
                          <span className="vp-contact-label">Phone</span>
                          {vendorData?.contactPhone ? (
                            <a href={`tel:${vendorData.contactPhone}`}>
                              {vendorData.contactPhone}
                            </a>
                          ) : (
                            <span className="vp-empty">Not set</span>
                          )}
                        </li>
                        <li>
                          <span className="vp-contact-label">Email</span>
                          {vendorData?.contactEmail ? (
                            <a href={`mailto:${vendorData.contactEmail}`}>
                              {vendorData.contactEmail}
                            </a>
                          ) : (
                            <span className="vp-empty">Not set</span>
                          )}
                        </li>
                      </ul>
                    </section>
                  </div>

                  <div className="vp-bento-col">
                    <section className="vp-card">
                      <h3 className="vp-card-title">About the business</h3>
                      {vendorData?.description ? (
                        <div className="vp-about">
                          {vendorData.description
                            .split(/\n{2,}/)
                            .map((para, i) => (
                              <p key={i}>{para}</p>
                            ))}
                        </div>
                      ) : (
                        <p className="vp-empty">
                          Nothing yet. Buyers read this first.
                        </p>
                      )}
                    </section>

                    <div className="vp-bento-pair">
                      <section className="vp-card">
                        <h3 className="vp-card-title">Portfolio images</h3>
                        {profileFileList.length ? (
                          <div className="vp-record-gallery">
                            {profileFileList.map((file) => (
                              <img
                                key={file.uid}
                                src={file.url || file.thumbUrl}
                                alt={file.name || "Portfolio image"}
                              />
                            ))}
                          </div>
                        ) : (
                          <p className="vp-empty">No images uploaded</p>
                        )}
                      </section>

                      <section className="vp-card vp-docs-card">
                        <h3 className="vp-card-title">Documents</h3>
                        {docs.length ? (
                          <div className="vp-doc-list">
                            {docs.map((d) => (
                              <a
                                key={d.url}
                                className="vp-doc-link"
                                href={d.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FilePdfOutlined />
                                <span className="vp-doc-name">{d.title}</span>
                                <span className="vp-doc-type">PDF</span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="vp-empty">None uploaded</p>
                        )}
                        <button
                          type="button"
                          className="vp-doc-manage"
                          onClick={() => setDocModalOpen(true)}
                        >
                          Manage documents
                        </button>
                      </section>
                    </div>
                  </div>
                </div>
              )}
            </Tabs.TabPane>

            {/* Tab 2: Products */}
            <Tabs.TabPane tab="My products" key="products">
              <div className="vp-section-head">
                <div>
                  <h2 className="vp-section-title">Listed products</h2>
                  <p className="vp-section-sub">
                    Moulds buyers can find and order from your lab.
                  </p>
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={!canAddProduct}
                  onClick={openAddProductModal}
                >
                  List a product
                </Button>
              </div>

              {/* Was a red error alert. Nothing has gone wrong: the trial
                  ended. Stated as a locked state, in the same tone as the
                  status strip above. */}
              {!canAddProduct && (
                <div className="vp-locked">
                  <p className="vp-locked-title">Listing is paused</p>
                  <p className="vp-locked-text">
                    Your free trial has ended. Subscribe to list new products or
                    edit the ones you already have. Existing listings stay
                    visible.
                  </p>
                </div>
              )}

              <Table
                dataSource={products}
                columns={productColumns}
                rowKey="_id"
                loading={productsLoading}
                pagination={{ pageSize: 10 }}
                /* Five columns don't fit a phone width. antd scrolls the
                   table body horizontally inside its own container instead
                   of letting the whole document overflow sideways. */
                scroll={{ x: 640 }}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>
      </div>

      {/* Documents: PDFs buyers can download from the public listing. */}
      <Modal
        title="Documents"
        open={docModalOpen}
        onCancel={() => setDocModalOpen(false)}
        centered
        width={560}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setDocModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={savingDocs}
            onClick={handleSaveDocs}
          >
            Save
          </Button>,
        ]}
      >
        <p className="vp-doc-intro">
          Catalogues, shade guides or price lists. Buyers download these from
          your public listing, so give each one a name they will understand.
          They are hidden while your listing is inactive.
        </p>

        {docs.length === 0 && (
          <p className="vp-doc-empty">No documents yet.</p>
        )}

        {docs.map((d, i) => (
          <div className="vp-doc-row" key={d.url}>
            <FilePdfOutlined className="vp-doc-icon" />
            <Input
              value={d.title}
              maxLength={120}
              placeholder="Name this document"
              onChange={(e) => {
                const title = e.target.value;
                setDocs((prev) =>
                  prev.map((x, xi) => (xi === i ? { ...x, title } : x))
                );
              }}
            />
            {/* Opens the file in a viewer without leaving the portal.
                Files upload on selection, so this works for a row added
                moments ago as well as one saved previously - the vendor can
                confirm they picked the right PDF before saving the list. */}
            <Button
              type="text"
              icon={<EyeOutlined />}
              aria-label={`View ${d.title}`}
              title="View"
              onClick={() => setViewingDoc(d)}
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Remove ${d.title}`}
              title="Remove"
              onClick={() =>
                setDocs((prev) => prev.filter((_, xi) => xi !== i))
              }
            />
          </div>
        ))}

        <Upload
          accept="application/pdf"
          showUploadList={false}
          beforeUpload={(file) => {
            handleDocUpload(file);
            /* false: we upload it ourselves so the URL can be captured
               and the antd list kept out of it. */
            return false;
          }}
        >
          <Button
            icon={<UploadOutlined />}
            loading={savingDocs}
            disabled={docs.length >= MAX_DOCS}
            className="vp-doc-add"
          >
            {docs.length >= MAX_DOCS
              ? `Limit of ${MAX_DOCS} reached`
              : "Upload PDF"}
          </Button>
        </Upload>
      </Modal>

      {/* PDF viewer. Sits above the documents modal so closing it returns
          to the list rather than dumping the vendor back on the page. */}
      <Modal
        title={viewingDoc?.title || "Document"}
        open={Boolean(viewingDoc)}
        onCancel={() => setViewingDoc(null)}
        centered
        width={900}
        destroyOnClose
        className="vp-doc-viewer"
        footer={[
          /* antd Button with an href renders a styled <a>. Hand-writing
             className="ant-btn" produced a bare link: antd v5 styles are
             CSS-in-JS under hashed class names, so the plain class matches
             nothing. Not every browser renders a PDF inline - iOS Safari
             refuses inside an iframe - so this escape must be visible. */
          <Button
            key="open"
            href={viewingDoc?.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in new tab
          </Button>,
          <Button key="close" type="primary" onClick={() => setViewingDoc(null)}>
            Close
          </Button>,
        ]}
      >
        {viewingDoc && (
          <iframe
            className="vp-doc-frame"
            src={viewingDoc.url}
            title={viewingDoc.title || "Document"}
          />
        )}
      </Modal>

      {/* Create/Edit Product Modal */}
      <Modal
        title={editingProduct ? "Edit Product Details" : "List New Product"}
        open={isProductModalOpen}
        onCancel={() => setIsProductModalOpen(false)}
        footer={null}
        width={750}
        destroyOnClose
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 8, marginTop: 15 }}>
          <Form form={productForm} layout="vertical" onFinish={handleCreateOrUpdateProduct}>
            <Form.Item
              name="name"
              label="Product Name"
              rules={[{ required: true, message: "Please enter product name" }]}
            >
              <Input placeholder="Enter product name" />
            </Form.Item>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Form.Item name="alphaCode" label="Alpha Code" rules={[{ required: true, message: "Required" }]}>
                <Input placeholder="Alpha Code" />
              </Form.Item>
              <Form.Item name="mouldNumber" label="Mould Number">
                <Input placeholder="Mould #" />
              </Form.Item>
              <Form.Item name="mass" label="Mass (g)">
                <Input placeholder="Mass" />
              </Form.Item>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Form.Item name="height" label="Height (mm)">
                <Input placeholder="Product height" />
              </Form.Item>
              <Form.Item name="width" label="Width (mm)">
                <Input placeholder="Product width" />
              </Form.Item>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Form.Item name="color" label="Color / Shade" rules={[{ required: true, message: "Please select color" }]}>
                <Select
                  placeholder="Select Color"
                  onChange={(value) => {
                    if (value === "create_color") {
                      handleOpenCreateNameModal("color", "Color");
                      productForm.setFieldsValue({ color: undefined });
                    }
                  }}
                >
                  {colors.map((c) => (
                    <Select.Option key={c._id || c.name} value={c.name}>
                      {c.name}
                    </Select.Option>
                  ))}
                  <Select.Option value="create_color" style={{ color: "#1890ff", fontWeight: "bold" }}>
                    + Create New Color
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="shape" label="Tooth Shape" rules={[{ required: true, message: "Please select shape" }]}>
                <Select
                  placeholder="Select Shape"
                  onChange={(value) => {
                    if (value === "create_shape") {
                      handleOpenCreateNameModal("shape", "Shape");
                      productForm.setFieldsValue({ shape: undefined });
                    }
                  }}
                >
                  {shapes.map((s) => (
                    <Select.Option key={s._id || s.name} value={s.name}>
                      {s.name}
                    </Select.Option>
                  ))}
                  <Select.Option value="create_shape" style={{ color: "#1890ff", fontWeight: "bold" }}>
                    + Create New Shape
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="case" label="Denture Case type" rules={[{ required: true, message: "Please select case type" }]}>
                <Select
                  placeholder="Select Case"
                  onChange={(value) => {
                    if (value === "create_case") {
                      handleOpenCreateNameModal("case", "Case");
                      productForm.setFieldsValue({ case: undefined });
                    }
                  }}
                >
                  {cases.map((c) => (
                    <Select.Option key={c._id || c.name} value={c.name}>
                      {c.name}
                    </Select.Option>
                  ))}
                  <Select.Option value="create_case" style={{ color: "#1890ff", fontWeight: "bold" }}>
                    + Create New Case
                  </Select.Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item name="pdfLink" label="PDF Info / Catalogue Link">
              <Input prefix={<FilePdfOutlined />} placeholder="URL to product details PDF" />
            </Form.Item>

            <Form.Item name="suggestions" label="Compatible Suggestions (comma-separated Alpha Codes)">
              <Input placeholder="e.g. ALPHA002, ALPHA003" />
            </Form.Item>

            {/* <Form.Item name="notAvail" label="Countries Not Available In (comma-separated)">
              <Input placeholder="e.g. Canada, Germany" />
            </Form.Item> */}

            <Form.Item name="description" label="Product Description">
              <Input.TextArea rows={3} placeholder="Write details about the mold, composition, aesthetics..." />
            </Form.Item>

            <Form.Item label="Product Images">
              <Upload
                listType="picture-card"
                fileList={productFileList}
                onChange={({ fileList: list }) => {
                  const updatedList = list.map(file => {
                    if (file.status === "done" && file.response && !file.url) {
                      file.url = file.response.fileUrl;
                    }
                    if (!file.url && !file.thumbUrl && file.originFileObj) {
                      file.thumbUrl = URL.createObjectURL(file.originFileObj);
                    }
                    return file;
                  });
                  setProductFileList(updatedList);
                }}
                customRequest={handleCustomUpload}
                onRemove={(file) => {
                  setProductFileList((prev) => prev.filter((item) => item.uid !== file.uid));
                }}
              >
                {productFileList.length < 4 && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
            </Form.Item>

            <Button type="primary" htmlType="submit" size="large" block loading={submittingProduct} style={{ width: "100%" }}>
              {editingProduct ? "Save Changes" : "Create Listing"}
            </Button>
          </Form>
        </div>
      </Modal>

      {/* Create Custom Dropdown Option Modal */}
      <Modal
        title={`Create New ${createNameType}`}
        open={showCreateNameModal}
        onCancel={() => setShowCreateNameModal(false)}
        footer={[
          <div key="footer" style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 15 }}>
            <Button onClick={() => setShowCreateNameModal(false)} disabled={creatingOption} style={{ width: "120px" }}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleCreateOption} loading={creatingOption} style={{ width: "120px" }}>
              OK
            </Button>
          </div>
        ]}
        destroyOnClose
      >
        <Input
          placeholder={`Enter ${createNameType.toLowerCase()} name`}
          value={newOptionValue}
          onChange={(e) => setNewOptionValue(e.target.value)}
          style={{ marginTop: 10 }}
        />
      </Modal>

      {/* Upgrade Subscription Modal */}
      <Modal
        title="Upgrade your subscription"
        open={upgradeModalOpen}
        onCancel={() => setUpgradeModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        {upgradeLoading ? (
          <p className="vp-upgrade-note">Loading plans…</p>
        ) : (
          <>
            {upgradeInfo?.currentPlan && (
              <p className="vp-upgrade-current">
                Current plan: <strong>{upgradeInfo.currentPlan.name}</strong> — $
                {upgradeInfo.currentPlan.price}
                {upgradeInfo.currentPeriodEnd &&
                  ` · renews ${new Date(upgradeInfo.currentPeriodEnd).toLocaleDateString()}`}
              </p>
            )}

            {(upgradeInfo?.upgradePlans || []).length === 0 ? (
              <p className="vp-upgrade-note">
                {upgradeInfo?.hasSubscription
                  ? "You are already on the highest plan available."
                  : "No plans available right now."}
              </p>
            ) : (
              <>
                <p className="vp-upgrade-note">
                  {upgradeInfo?.hasSubscription
                    ? "You'll be charged only the prorated difference for the rest of this billing period. Your renewal date stays the same."
                    : "Choose a plan to start a subscription."}
                </p>
                <div className="vp-upgrade-list">
                  {upgradeInfo.upgradePlans.map((plan) => (
                    <div key={plan._id} className="vp-upgrade-plan">
                      <div className="vp-upgrade-plan-info">
                        <div className="vp-upgrade-plan-name">{plan.name}</div>
                        <div className="vp-upgrade-plan-meta">
                          ${plan.price}
                          {plan.duration ? ` / ${plan.duration} mo` : ""}
                          {plan.numOfProduct !== undefined &&
                            ` · ${plan.numOfProduct === -1 ? "Unlimited" : plan.numOfProduct} products`}
                        </div>
                        {Array.isArray(plan.description) && plan.description.length > 0 && (
                          <ul className="vp-upgrade-plan-desc">
                            {plan.description.map((d, i) => (
                              <li key={i}>{d}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <Button
                        type="primary"
                        loading={upgradingPlanId === plan._id}
                        disabled={upgradingPlanId && upgradingPlanId !== plan._id}
                        onClick={() => handleUpgrade(plan)}
                      >
                        {upgradeInfo?.hasSubscription ? "Upgrade" : "Subscribe"}
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default VendorDashboard;
